import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  getTodos,
  getCurrentUser,
  getProfile,
  createTodo,
  updateTodo,
  checkTodoExistsForDate,
  getVote,
  upsertVote,
  deleteVote,
  getTodoVotes,
} from '../utils/supabase';

const Todo = () => {
  const [todos, setTodos] = useState([]);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [newTodo, setNewTodo] = useState({
    content: '',
    todo_date: new Date().toISOString().split('T')[0],
  });
  const [userVotes, setUserVotes] = useState({});
  const [todoVotes, setTodoVotes] = useState({});
  const formRef = useRef(null);

  const LIMIT = 20;
  const LOAD_MORE = 10;

  useEffect(() => {
    loadUserAndTodos();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      loadTodos(true);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadUserAndTodos = async () => {
    const { user } = await getCurrentUser();
    setUser(user);
    if (user) {
      const { data } = await getProfile(user.id);
      setProfile(data);
    }
    await loadTodos(true);
  };

  const loadTodos = async (reset = false) => {
    setLoading(true);
    const currentOffset = reset ? 0 : offset;
    const { data, error } = await getTodos(LIMIT, currentOffset);
    if (error) {
      console.error('Error loading todos:', error);
      setLoading(false);
      return;
    }

    if (reset) {
      setTodos(data || []);
      setOffset(LIMIT);
      setHasMore((data || []).length === LIMIT);
    } else {
      setTodos((prev) => [...prev, ...(data || [])]);
      setOffset((prev) => prev + (data || []).length);
      setHasMore((data || []).length === LIMIT);
    }

    // Load votes for each todo
    if (data && data.length > 0) {
      await loadVotesForTodos(data);
    }
    setLoading(false);
  };

  const loadVotesForTodos = async (todoList) => {
    if (!user) return;
    const votesObj = {};
    const countsObj = {};

    for (const todo of todoList) {
      // Get user's vote
      const { data: userVote } = await getVote(todo.id, user.id);
      votesObj[todo.id] = userVote?.vote_type || null;

      // Get vote counts
      const { data: allVotes } = await getTodoVotes(todo.id);
      const upvotes = allVotes?.filter(v => v.vote_type === 'upvote').length || 0;
      const downvotes = allVotes?.filter(v => v.vote_type === 'downvote').length || 0;
      countsObj[todo.id] = { upvotes, downvotes };
    }

    setUserVotes(prev => ({ ...prev, ...votesObj }));
    setTodoVotes(prev => ({ ...prev, ...countsObj }));
  };

  const handleVote = async (todoId, voteType) => {
    if (!user) {
      setError('Please sign in to vote');
      return;
    }

    const currentVote = userVotes[todoId];
    
    if (currentVote === voteType) {
      // Remove vote
      await deleteVote(todoId, user.id);
      setUserVotes(prev => ({ ...prev, [todoId]: null }));
      // Update counts
      setTodoVotes(prev => ({
        ...prev,
        [todoId]: {
          upvotes: prev[todoId]?.upvotes - (voteType === 'upvote' ? 1 : 0),
          downvotes: prev[todoId]?.downvotes - (voteType === 'downvote' ? 1 : 0),
        }
      }));
    } else {
      // Add or change vote
      await upsertVote(todoId, user.id, voteType);
      setUserVotes(prev => ({ ...prev, [todoId]: voteType }));
      // Update counts
      setTodoVotes(prev => {
        const newCounts = { ...prev };
        if (currentVote) {
          newCounts[todoId] = {
            upvotes: newCounts[todoId]?.upvotes - (currentVote === 'upvote' ? 1 : 0),
            downvotes: newCounts[todoId]?.downvotes - (currentVote === 'downvote' ? 1 : 0),
          };
        }
        newCounts[todoId] = {
          upvotes: (newCounts[todoId]?.upvotes || 0) + (voteType === 'upvote' ? 1 : 0),
          downvotes: (newCounts[todoId]?.downvotes || 0) + (voteType === 'downvote' ? 1 : 0),
        };
        return newCounts;
      });
    }
  };

  const handleCreateTodo = async (e) => {
    e.preventDefault();
    if (!user) {
      setError('Please sign in to create a todo');
      return;
    }

    setSubmitting(true);
    setError('');

    // Check if todo exists for this date
    const { data: existing } = await checkTodoExistsForDate(user.id, newTodo.todo_date);
    if (existing) {
      setError('You already have a todo for this date');
      setSubmitting(false);
      return;
    }

    const todoData = {
      user_id: user.id,
      content: newTodo.content,
      todo_date: newTodo.todo_date,
      is_completed: false,
    };

    const { data, error } = await createTodo(todoData);
    if (error) {
      setError(error.message);
      setSubmitting(false);
      return;
    }

    // Add to list with profile info
    setTodos(prev => [{
      ...data,
      profiles: {
        id: profile.id,
        full_name: profile.full_name,
        avatar_url: profile.avatar_url,
      }
    }, ...prev]);

    setNewTodo({
      content: '',
      todo_date: new Date().toISOString().split('T')[0],
    });
    setShowForm(false);
    setSubmitting(false);
  };

  const handleCompleteTodo = async (todoId, isCompleted) => {
    const now = new Date();
    const hour = now.getHours();
    
    // Only allow completion between 8pm and 12am
    if (hour < 20 || hour >= 24) {
      setError('You can only mark todos as complete between 8pm and 12am');
      return;
    }

    const { error } = await updateTodo(todoId, { is_completed: !isCompleted });
    if (error) {
      setError(error.message);
      return;
    }

    setTodos(prev =>
      prev.map(todo =>
        todo.id === todoId ? { ...todo, is_completed: !isCompleted } : todo
      )
    );
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.getTime() === today.getTime()) return 'Today';
    if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
    if (date.getTime() === yesterday.getTime()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  const canComplete = () => {
    const now = new Date();
    const hour = now.getHours();
    return hour >= 20 && hour < 24;
  };

  return (
    <div className="min-h-[calc(100vh-64px)] pb-24">
      <div className="container-custom py-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold">📋 Todos</h1>
            <p className="text-gray-500 text-sm">Latest todos from everyone</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
          >
            {showForm ? 'Cancel' : '+ New Todo'}
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
            <button onClick={() => setError('')} className="ml-2">✕</button>
          </div>
        )}

        {/* Create Todo Form */}
        {showForm && (
          <div ref={formRef} className="bg-white rounded-xl p-6 shadow-sm mb-6 animate-fade-in">
            <form onSubmit={handleCreateTodo}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Todo Content</label>
                  <textarea
                    value={newTodo.content}
                    onChange={(e) => setNewTodo({ ...newTodo, content: e.target.value })}
                    placeholder="What do you want to do?"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    rows="3"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date</label>
                  <input
                    type="date"
                    value={newTodo.todo_date}
                    onChange={(e) => setNewTodo({ ...newTodo, todo_date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    max={new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
                >
                  {submitting ? 'Creating...' : 'Create Todo'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Todo List */}
        {loading && todos.length === 0 ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : todos.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <div className="text-4xl mb-4">📝</div>
            <p>No todos yet. Be the first to create one!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {todos.map((todo) => (
              <div
                key={todo.id}
                className={`bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition ${
                  todo.is_completed ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* User Avatar */}
                  <Link to={`/profile/${todo.user_id}`}>
                    <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
                      {getInitials(todo.profiles?.full_name)}
                    </div>
                  </Link>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Link
                        to={`/profile/${todo.user_id}`}
                        className="font-medium hover:text-primary transition"
                      >
                        {todo.profiles?.full_name || 'Unknown User'}
                      </Link>
                      <span className="text-xs text-gray-400">
                        {formatDate(todo.todo_date)}
                      </span>
                      {isToday(todo.todo_date) && (
                        <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">
                          Today
                        </span>
                      )}
                    </div>
                    <p className="text-gray-800 mb-2">{todo.content}</p>
                    
                    <div className="flex flex-wrap items-center gap-4">
                      {/* Complete button */}
                      {todo.user_id === user?.id && isToday(todo.todo_date) && (
                        <button
                          onClick={() => handleCompleteTodo(todo.id, todo.is_completed)}
                          disabled={!canComplete()}
                          className={`text-sm px-3 py-1 rounded-full transition ${
                            todo.is_completed
                              ? 'bg-green-100 text-green-600'
                              : canComplete()
                              ? 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-600'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {todo.is_completed ? '✓ Completed' : 'Mark Complete'}
                        </button>
                      )}

                      {/* Vote buttons */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleVote(todo.id, 'upvote')}
                          className={`flex items-center gap-1 px-2 py-1 rounded-full transition ${
                            userVotes[todo.id] === 'upvote'
                              ? 'bg-green-100 text-green-600'
                              : 'hover:bg-gray-100 text-gray-500'
                          }`}
                        >
                          👍 {todoVotes[todo.id]?.upvotes || 0}
                        </button>
                        <button
                          onClick={() => handleVote(todo.id, 'downvote')}
                          className={`flex items-center gap-1 px-2 py-1 rounded-full transition ${
                            userVotes[todo.id] === 'downvote'
                              ? 'bg-red-100 text-red-600'
                              : 'hover:bg-gray-100 text-gray-500'
                          }`}
                        >
                          👎 {todoVotes[todo.id]?.downvotes || 0}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Load More */}
            {hasMore && (
              <button
                onClick={() => loadTodos(false)}
                disabled={loading}
                className="w-full py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Todo;
